import React from 'react';
import T from 'prop-types';
import { isEmpty } from 'lodash-es';

import skeletons from './Skeletons';
import TakeawaysContent from './TakeawaysContent';
import ForecastChangeBox, { ForecastRestrictedBox } from '../../../../finance-page/components/KpiForecasts/ForecastChangeBox';
import { useShadowLine } from '../../../../../components/DropdownSet';

import { getForecastLabel } from '../../config/forecasts';
import { format, formatQuarterYear } from '../../../../../utils/date';
import { numberToPercentage } from '../../../../../utils/formatter';

const colorByValue = (value) => {
  const parsedValue = parseFloat(value);
  if (parsedValue > 0) {
    return '#06804D';
  }

  if (parsedValue < 0) {
    return '#C23F42';
  }

  return '#363D54';
};

const ForecastsSidebar = ({
  title,
  activeForecast,
  pending,
  allowForecastsWhitoutConsensus,
}) => {
  const {
    change,
    consensus_yoy,
    delta,
    forecast_yoy,
    forecast_index,
    final_forecast,
    forecast_period,
    forecast_published_at,
    key_takeaways,
    kpi_metric_name,
  } = !isEmpty(activeForecast) ? activeForecast.forecasts : {};

  const [shadowLine, onScroll] = useShadowLine();

  const forecastName = getForecastLabel({ forecast_index, final_forecast });

  const renderForecastValues = () => {
    if (allowForecastsWhitoutConsensus) {
      return (
        <div className="forecasts-main-sidebar__values">
          <ForecastChangeBox
            value={forecast_yoy}
            change={change}
            labelValue="Forecast"
            labelChange="Change"
          />

          <ForecastRestrictedBox
            label="Delta to Consensus"
            change={delta}
          />
        </div>
      );
    }

    return (
      <div className="forecasts-main-sidebar__values">
        <div className="forecasts-main-sidebar__values-item">
          <div className="forecasts-main-sidebar__values-item-head">
            Forecast
          </div>
          <div className="forecasts-main-sidebar__values-item-value">
            {forecast_yoy
              ? <span>{numberToPercentage(forecast_yoy, { precision: 0 })}</span>
              : <span className="text-quiet">—</span>}
            <span className="forecasts-main-sidebar__values-item-value-sub">YoY</span>
          </div>
        </div>
        <div className="forecasts-main-sidebar__values-item">
          <div className="forecasts-main-sidebar__values-item-head">
            Change in Forecast
          </div>
          <div className="forecasts-main-sidebar__values-item-value m-l-0">
            {change
              ? <span style={{ color: colorByValue(change) }}>{numberToPercentage(change, { precision: 1 })}</span>
              : <span className="text-quiet">—</span>}
          </div>
        </div>
        <div className="forecasts-main-sidebar__values-item">
          <div className="forecasts-main-sidebar__values-item-head">
            <b>Consensus</b>
          </div>
          <div className="forecasts-main-sidebar__values-item-value">
            {consensus_yoy
              ? <span>{consensus_yoy === -100 ? 'N/A' : numberToPercentage(consensus_yoy, { precision: 0 })}</span>
              : <span className="text-quiet">—</span>}
            <span className="forecasts-main-sidebar__values-item-value-sub">YoY</span>
          </div>
        </div>
        <div className="forecasts-main-sidebar__values-item">
          <div className="forecasts-main-sidebar__values-item-head">
            Delta to Consensus
          </div>
          <div className="forecasts-main-sidebar__values-item-value m-l-0">
            {delta
              ? <span style={{ color: delta !== 'Infinity%' ? colorByValue(delta) : '#363D54' }}>{delta === 'Infinity%' ? 'N/A' : delta}</span>
              : <span className="text-quiet">—</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="forecasts-main-sidebar">
      <div className="forecasts-main-sidebar__stats">
        {pending
          ? skeletons.forecastStats
          : (
            <div className="forecasts-main-sidebar__stats-left">
              <div className="forecasts-main-sidebar__stats-left-head">
                {title}
              </div>
              <div className="forecasts-main-sidebar__stats-left-item text-truncate">
                {kpi_metric_name}
              </div>
              <div className="forecasts-main-sidebar__stats-left-item">
                {formatQuarterYear(forecast_period)} · {forecastName} · {format(forecast_published_at)}
              </div>
            </div>
          )}
      </div>

      {pending
        ? skeletons.forecastsSidebarValues(allowForecastsWhitoutConsensus)
        : renderForecastValues()}

      <div className="forecasts-main-sidebar__takeaways">
        <div className="forecasts-main-sidebar__takeaways-head">
          <div className="forecasts-main-sidebar__takeaways-header">
            Key Takeaways
          </div>
          {shadowLine}
        </div>
        <div className="forecasts-main-sidebar__takeaways-body" onScroll={onScroll}>
          {pending ? skeletons.forecastsSidebarTakeaways : (
            <TakeawaysContent
              pending={pending}
              key_takeaways={key_takeaways}
            />
          )}
        </div>
      </div>
    </div>
  );
};

ForecastsSidebar.propTypes = {
  title: T.string,
  activeForecast: T.object,
  pending: T.bool,
  allowForecastsWhitoutConsensus: T.bool,
};

export default ForecastsSidebar;
